import { useState } from "react";
import { ButtonPrimary } from "../../components/NewButtons";
import { MultiSelectModal } from "../../components/MultiSelectModal";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { MenuRecommendedProduct } from "./models/Menus";
import {
  useGetMenuAvailableProducts,
  useUpdateMenuRecommendedProducts,
} from "./service/menus-api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { useParams } from "react-router-dom";
import { ProductsFilter } from "../products/ProductsFilter";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";

type Props = {
  currentRecommendedProducts: MenuRecommendedProduct[];
};

export const AddRecommendedProductsSelector = ({
  currentRecommendedProducts,
}: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const { tags, categories } = useSearchParamsManipulation();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [modalOpen, setModalOpen] = useState(false);
  const {
    data: products,
    isLoading,
    error: getMenuAvailableProductsError,
  } = useGetMenuAvailableProducts(organizationKeyName, true, id, {
    tags: tags?.split(",") || [],
  });

  const {
    mutateAsync: updateRecommendedProducts,
    isLoading: isLoadingUpdate,
  } = useUpdateMenuRecommendedProducts(id);

  const productOptions = (products || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(
      (p) => !currentRecommendedProducts.find((crp) => crp.product.id === p.id)
    )
    .filter((p) => {
      if (categories && categories.length > 0) {
        return categories.includes(p.category.name);
      }
      return true;
    })
    .map((p) => ({
      value: p.id,
      label: p.name,
    }));

  useHandleFetchError(getMenuAvailableProductsError);

  return (
    <>
      <ButtonPrimary
        onClick={() => {
          setModalOpen(true);
        }}
      >
        Add recommended products
      </ButtonPrimary>
      <MultiSelectModal
        options={productOptions}
        header="Add recommended products to this menu"
        isLoading={isLoading}
        isLoadingSave={isLoadingUpdate}
        isOpen={modalOpen}
        topComponent={<ProductsFilter />}
        onClose={() => setModalOpen(false)}
        onSave={async (addedProducts: string[]) => {
          const currentAddedRecommendedProducts = currentRecommendedProducts.map(
            (p) => ({
              id: p.product.id,
              type: p.type,
              sortKey: p.sortKey,
            })
          );
          const recommendedProducts = (products || [])
            .filter((p) => addedProducts.includes(p.id))
            .map((p, i) => ({
              id: p.id,
              type: "add_to_order",
              sortKey: currentAddedRecommendedProducts.length + i + 1,
            }));
          try {
            await updateRecommendedProducts({
              recommendedProducts: [
                ...recommendedProducts,
                ...currentAddedRecommendedProducts,
              ] as MenuRecommendedProduct[],
              menuId: id,
              organizationKeyName,
            });
            toastr.success(
              "Success",
              "Recommended products added successfully"
            );
            setModalOpen(false);
          } catch (error: unknown) {
            const message = getErrorMessage(error);
            toastr.error("Error", message);
          }
        }}
      />
    </>
  );
};
